/**
 * Tiny per-tab TTL cache for the read-through report bridge (PR6).
 *
 * The chatboard read path fetches a stored report by its pointer's row_key (or a
 * resolution by qkey). Those reads are point-reads against BigTable / BigQuery
 * and are stable for the life of a stored row, so a short client-side TTL cache
 * collapses repeated renders (open report → close → reopen, re-select the same
 * forecast) into a single network round-trip.
 *
 * A cached value may be `null` — the deliberate "negative" entry the fetch
 * helpers write on a 404 / miss so a fallback-to-server_ctx render doesn't
 * re-fetch on every render within the TTL. `get` therefore returns:
 *   - the stored value (possibly `null`) when present AND fresh,
 *   - `undefined` when absent OR expired (the caller should fetch).
 *
 * Pure + framework-free (no React) — `now` is injectable for deterministic tests.
 *
 * @param {{ ttlMs?: number, now?: () => number }} [opts]
 * @returns {{ get:(k:string)=>*, set:(k:string,v:*)=>*, clear:()=>void }}
 */
export function makeTtlCache({ ttlMs = 60000, now = () => Date.now() } = {}) {
  const store = new Map();
  return {
    get(key) {
      const e = store.get(key);
      if (!e) return undefined;
      if (now() - e.at > ttlMs) {
        store.delete(key); // evict the stale entry so the Map doesn't grow
        return undefined;
      }
      return e.val;
    },
    set(key, val) {
      store.set(key, { val, at: now() });
      return val;
    },
    clear() {
      store.clear();
    },
  };
}
